import { useCallback } from 'react';
import API from '../services/apiService';
import cookies from '../utils/cookies';
import useBrand from '../hooks/useBrand';
import type { Cart, CartProduct } from '../types/cart';
import type { PayloadObj } from '../types/services';
import type { ProductState } from '../types/product';
import { ecomAddProduct, ecomRemoveProduct } from '../utils/ecomHelper';

// TODO: define this interface for gobal usage
interface UseCartActionsArgs {
  // productData: PayloadObj;
  setState?: React.Dispatch<React.SetStateAction<ProductState>>;
  catalogDispatch: React.Dispatch<any>;
}

export function useCartActions({ setState, catalogDispatch }: UseCartActionsArgs) {
  const brand = useBrand();
  const domain = brand?.domain;

  // Add to cart
  const addToCart = useCallback(
    (productData: PayloadObj, callback?: () => void) => {
      setState?.((prevState) => ({
        ...prevState,
        isUpdatingCart: true,
      }));

      catalogDispatch({
        type: 'UPDATE_CART',
        payload: {
          isUpdating: true,
        },
      });

      const cartData = {
        cartId: productData?.productInCart?.cartId || cookies.get('cartId'),
        productId: productData?.productId,
        quantity: productData.quantity,
        pricingUsed: cookies.get('_up4tp'),
        groupId: cookies.get('_up4gid'),
        coupon: cookies.get('_up4cc'),
        sourceHost: domain,
        selectedUpsellProductIds: productData.selectedUpsellProductIds,
      };

      API.addProduct(cartData)
        .then((response) => {
          updateStateAndStorage(response.data, productData.productId);

          const item = response.data.cartProducts?.find((item: CartProduct) => item.productId === productData.productId);
          ecomAddProduct(item);

          if (callback) callback();
        })
        .catch((error) => {
          // setProductState((prevState) => ({ ...prevState, isUpdatingCart: false }));
          console.error(error);
        });
    },
    [domain, catalogDispatch]
  );

  // Remove from cart
  const removeProduct = useCallback(
    (productData: PayloadObj, callback?: () => void) => {
      setState?.((prevState) => ({
        ...prevState,
        isUpdatingCart: true,
      }));

      catalogDispatch({
        type: 'UPDATE_CART',
        payload: {
          isUpdating: true,
        },
      });

      const cartId = productData.productInCart?.cartId || cookies.get('cartId');
      const cartProductId = productData.productInCart?.cart_ProductId;

      if (cartId && cartProductId) {
        API.removeProduct(cartId, cartProductId)
          .then((response) => {
            updateStateAndStorage(response.data, productData.productId);

            ecomRemoveProduct(productData.productInCart as CartProduct);

            if (callback) callback();
          })
          .catch((error) => {
            // setProductState((prevState) => ({ ...prevState, isUpdatingCart: false }));
            console.error(error);
          });
      } else {
        console.error('Cannot delete the product: missing cart information!');
      }
    },
    [addToCart, catalogDispatch]
  );

  // Update cart
  const updateCart = useCallback(
    (productData: PayloadObj, callback?: () => void) => {
      setState?.((prevState) => ({
        ...prevState,
        isUpdatingCart: true,
      }));

      catalogDispatch({
        type: 'UPDATE_CART',
        payload: {
          isUpdating: true,
        },
      });

      const cartData = {
        cartId: productData?.productInCart?.cartId || cookies.get('cartId'),
        productId: productData?.productId,
        cartProductId: productData?.productInCart?.cart_ProductId,
        quantity: productData.quantity,
        isSelected: productData?.isSelected,
      };

      API.updateProduct(cartData)
        .then((response) => {
          updateStateAndStorage(response.data, productData.productId);

          const item = response.data.cartProducts?.find((item: CartProduct) => item.productId === productData.productId);
          ecomAddProduct(item);

          if (callback) callback();
        })
        .catch((error) => {
          // setProductState((prevState) => ({ ...prevState, isUpdatingCart: false }));
          console.error(error);
        });
    },
    [catalogDispatch]
  );

  // Update upsells
  const updateUpsells = useCallback(
    (productData: PayloadObj, callback?: () => void) => {
      setState?.((prevState) => ({
        ...prevState,
        isUpdatingCart: true,
      }));

      catalogDispatch({
        type: 'UPDATE_CART',
        payload: {
          isUpdating: true,
        },
      });

      const cartId = productData?.productInCart?.cartId || cookies.get('cartId');
      const cartProductId = productData.productInCart?.cart_ProductId;

      if (cartId && cartProductId) {
        API.removeProduct(cartId, cartProductId)
          .then(() => {
            ecomRemoveProduct(productData.productInCart as CartProduct);
            addToCart(productData, callback);
          })
          .catch((error) => {
            // setProductState((prevState) => ({ ...prevState, isUpdatingCart: false }));
            console.error(error);
          });
      } else {
        console.error('Cannot delete the product: missing cart information!');
      }
    },
    [addToCart, catalogDispatch]
  );

  // Get cart
  const getCart = useCallback(
    (cartId: string, callback?: () => void, controller?: AbortController) => {
      const id = cartId || cookies.get('cartId');
      if (!id) {
        console.error('No cartId found');
        return;
      }

      catalogDispatch({
        type: 'UPDATE_CART',
        payload: {
          isUpdating: true,
        },
      });

      API.getCart(id, controller?.signal)
        .then((response) => {
          const cartData: Cart = response.data;

          catalogDispatch({
            type: 'UPDATE_CART',
            payload: {
              ...cartData,
              isUpdating: false,
            },
          });

          // setting localCart in sessionStorage, use for persisting cart faster on page reload
          // and for setting initial cart state in CatalogProvider
          sessionStorage.setItem(
            'localCart',
            JSON.stringify({
              ...cartData,
              isUpdating: false,
            })
          );

          if (callback) callback();
        })
        .catch((error) => {
          console.error('Error fetching cart');

          if (error?.response?.data || error?.message) {
            console.error('Error fetching cart:', error?.message);
            console.error('Error fetching cart:', error);
          }
          if (error?.response?.status === 404) {
            // delete stored cart data
            cookies.delete('cartId');
            sessionStorage.removeItem('localCart');

            catalogDispatch({
              type: 'UPDATE_CART',
              payload: null,
            });
          }
        });

      // return controller so the caller can abort if needed
      if (controller) {
        return controller;
      }
    },
    [catalogDispatch]
  );

  // Update state and storage
  const updateStateAndStorage = useCallback(
    (cartData: Cart, productId?: string | null | undefined) => {
      if (cartData.cartProducts.length > 0) {
        cookies.set('cartId', cartData.cartId, 14);
      } else {
        cookies.delete('cartId');
        sessionStorage.removeItem('localCart');
      }

      const thisProductInCart = cartData?.cartProducts?.find((item: CartProduct) => item.productId === productId);

      setState?.((prevState) => ({
        ...prevState,
        productInCart: thisProductInCart,
        isUpdatingCart: false,
        isUpsellUpdated: false,
        isQuantityUpdated: false,
      }));

      catalogDispatch({
        type: 'UPDATE_CART',
        payload: {
          ...cartData,
          isUpdating: false,
        },
      });

      // setting localCart in sessionStorage, use for persisting cart faster on page reload
      // and for setting initial cart state in CatalogProvider
      sessionStorage.setItem(
        'localCart',
        JSON.stringify({
          ...cartData,
          isUpdating: false,
        })
      );

      // this can be moved out into the callback function for further optimization
      catalogDispatch({
        type: 'UX',
        payload: { showCartWidget: true },
      });
    },
    [setState, catalogDispatch]
  );

  return { addToCart, removeProduct, updateCart, updateUpsells, getCart };
}
